import {
  checkIfAuthenticated,
  StoreState,
  LOGIN_ROUTE,
  REGISTER_ROUTE,
  REGISTERED_ROUTE,
  VERIFY_ROUTE,
  VERIFIED_ROUTE,
  REGISTER_BY_PRODUCT_ROUTE,
  REGISTERED_BY_PRODUCT_ROUTE,
  VERIFY_CODE_USER_BY_PRODUCT_ROUTE,
  VERIFIED_BY_PRODUCT_ROUTE,
  FORGOT_PASSWORD_ROUTE,
  FORGOT_PASSWORD_SENT_ROUTE
} from '@futrli/auth';
import { ROUTES, WORKSPACES_AND_ORGANISATION_BASE, TrackedUrl, ROUTE_PARAMS } from '@futrli/shared-services';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { DEFAULT_PAGE_TITLE } from '../../core/constants';
import { tracker } from '../../core/defaults';
import { useSelector } from 'react-redux';
import { hasBlacklistedParam, getInterpolatedTitle, sanitazeStr, findPathMatch } from '../../utils/global-page-tracker';
import { Location } from 'history';
import { useIntl } from 'react-intl';
import { FEDERATED_SIGN_IN_TRIGGER, SSO_EMBED_TRIGGER } from '@futrli/auth/core/constants';

let lastPageEventTitle = '';

/**
 * Global page tracker component
 */
const GlobalPageTrackerFC: React.FC = () => {
  const location = useLocation();
  const intl = useIntl();
  const isAutheticated = useSelector((state: StoreState) => {
    return checkIfAuthenticated(state.authReducers);
  });

  /** GLOBAL PAGE TRACKER ROUTES */
  const UNAUTHENTICATED_PAGE_TRACKER_URLS: TrackedUrl[] = useMemo(
    () => [
      {
        paths: [LOGIN_ROUTE],
        title: 'Login',
        displayedTitle: intl.formatMessage({ id: 'common.login', defaultMessage: 'Login' })
      },
      {
        paths: [REGISTER_ROUTE],
        title: 'Register',
        displayedTitle: intl.formatMessage({ id: 'common.register', defaultMessage: 'Register' })
      },
      {
        paths: [REGISTERED_ROUTE],
        title: 'Check your inbox - Registered',
        displayedTitle: intl.formatMessage({
          id: 'register.check_inbox_registered',
          defaultMessage: 'Check your inbox - Registered'
        })
      },
      {
        paths: [VERIFY_ROUTE],
        title: 'E-mail verification in progress',
        displayedTitle: intl.formatMessage({
          id: 'register.email_verification_in_progress',
          defaultMessage: 'E-mail verification in progress'
        })
      },
      {
        paths: [VERIFIED_ROUTE],
        title: 'E-mail verification completed',
        displayedTitle: intl.formatMessage({
          id: 'register.email_verification_completed',
          defaultMessage: 'E-mail verification completed'
        })
      },
      {
        paths: [REGISTER_BY_PRODUCT_ROUTE],
        title: `Register - :${ROUTE_PARAMS.PRODUCT_SLUG}`,
        displayedTitle: intl.formatMessage({ id: 'common.register', defaultMessage: 'Register' }),
        interpolateTitleParams: true
      },
      {
        paths: [REGISTERED_BY_PRODUCT_ROUTE],
        title: `Check your inbox - Registered :${ROUTE_PARAMS.PRODUCT_SLUG}`,
        displayedTitle: intl.formatMessage({
          id: 'register.check_inbox_registered',
          defaultMessage: 'Check your inbox - Registered'
        }),
        interpolateTitleParams: true
      },
      {
        paths: [VERIFY_CODE_USER_BY_PRODUCT_ROUTE],
        title: `E-mail verification in progress - :${ROUTE_PARAMS.PRODUCT_SLUG}`,
        displayedTitle: intl.formatMessage({
          id: 'register.email_verification_in_progress',
          defaultMessage: 'E-mail verification in progress'
        }),
        interpolateTitleParams: true
      },
      {
        paths: [VERIFIED_BY_PRODUCT_ROUTE],
        title: `E-mail verification completed - :${ROUTE_PARAMS.PRODUCT_SLUG}`,
        displayedTitle: intl.formatMessage({
          id: 'register.email_verification_completed',
          defaultMessage: 'E-mail verification completed'
        }),
        interpolateTitleParams: true
      },
      {
        paths: [FORGOT_PASSWORD_ROUTE],
        title: 'Forgot Password',
        displayedTitle: intl.formatMessage({
          id: 'forgot_password.forgot_password',
          defaultMessage: 'Forgot Password'
        })
      },
      {
        paths: [FORGOT_PASSWORD_SENT_ROUTE],
        title: 'Check your inbox - Forgot Password',
        displayedTitle: intl.formatMessage({
          id: 'forgot_password.check_inbox',
          defaultMessage: 'Check your inbox - Forgot Password'
        })
      },
      {
        paths: [FEDERATED_SIGN_IN_TRIGGER],
        title: 'Single Sign On automatic trigger',
        displayedTitle: intl.formatMessage({
          id: 'login.single_sign_on_automatic_trigger',
          defaultMessage: 'Single Sign On automatic trigger'
        })
      },
      {
        paths: [SSO_EMBED_TRIGGER],
        title: 'Single Sign On Embed automatic trigger',
        displayedTitle: intl.formatMessage({
          id: 'login.single_sign_on_embed_automatic_trigger',
          defaultMessage: 'Single Sign On Embed automatic trigger'
        })
      }
    ],
    [intl]
  );

  const BASE_PREDICT = useMemo(() => [WORKSPACES_AND_ORGANISATION_BASE, ROUTES.PREDICT], []);
  const BASE_PREDICT_FORECAST = useMemo(() => [...BASE_PREDICT, ROUTES.FORECASTS, ROUTES.FORECAST_BY_ID], [
    BASE_PREDICT
  ]);

  const PREDICT_PAGE_TRACKER_URLS: TrackedUrl[] = useMemo(
    () => [
      {
        paths: [...BASE_PREDICT, ROUTES.SYNCING_PROGRESS],
        title: 'Futrli syncing progress',
        displayedTitle: intl.formatMessage({
          id: 'sync-progress.futrli_syncing_progress',
          defaultMessage: 'Futrli syncing progress'
        })
      },
      {
        paths: [...BASE_PREDICT, ROUTES.TRIAL_EXPIRED],
        title: 'Futrli trial expired',
        displayedTitle: intl.formatMessage({
          id: 'trial_expired.futrli_trial_expired',
          defaultMessage: 'Futrli trial expired'
        })
      },
      {
        paths: [...BASE_PREDICT, ROUTES.HOME],
        title: 'Home page',
        displayedTitle: intl.formatMessage({ id: 'home.home_page', defaultMessage: 'Home page' })
      },
      // Drivers
      {
        paths: [...BASE_PREDICT, ROUTES.NON_FINANCIAL_DRIVERS],
        title: 'Drivers',
        displayedTitle: intl.formatMessage({ id: 'reporting.grouping.drivers', defaultMessage: 'Drivers' })
      },
      // Organisation settings
      {
        paths: [ROUTES.WORKSPACE_BY_ID, ROUTES.WORKSPACE_BY_ID_SETTINGS_ROUTE, ROUTES.ORGANISATIONS_ROUTE],
        title: 'Organisations',
        displayedTitle: intl.formatMessage({ id: 'common.organisations', defaultMessage: 'Organisations' })
      },
      {
        paths: [ROUTES.WORKSPACE_BY_ID, ROUTES.WORKSPACE_BY_ID_SETTINGS_ROUTE, ROUTES.USERS_ROUTE],
        title: 'Users',
        displayedTitle: intl.formatMessage({ id: 'common.users', defaultMessage: 'Users' })
      },
      // Profile page
      {
        paths: [ROUTES.MY_PROFILE],
        title: 'Profile',
        displayedTitle: intl.formatMessage({ id: 'profile.profile', defaultMessage: 'Profile' })
      },
      // Reporting
      {
        paths: [...BASE_PREDICT, ROUTES.REPORTING],
        title: 'Reporting - Home',
        displayedTitle: intl.formatMessage({ id: 'reporting.reporting_home', defaultMessage: 'Reporting - Home' })
      },
      {
        paths: [...BASE_PREDICT, ROUTES.REPORTING_VIEW],
        title: 'Reporting - View',
        displayedTitle: intl.formatMessage({ id: 'reporting.reporting_view', defaultMessage: 'Reporting - View' })
      },
      {
        paths: [...BASE_PREDICT, ROUTES.REPORTING_EDIT],
        title: 'Reporting - Edit',
        displayedTitle: intl.formatMessage({ id: 'reporting.reporting_edit', defaultMessage: 'Reporting - Edit' })
      },
      {
        paths: [...BASE_PREDICT, ROUTES.REPORT_TEMPLATE_VIEW],
        title: 'Reporting - View Template',
        displayedTitle: intl.formatMessage({
          id: 'reporting.reporting_view_template',
          defaultMessage: 'Reporting - View Template'
        })
      },
      {
        paths: [...BASE_PREDICT, ROUTES.REPORT_TEMPLATE_EDIT],
        title: 'Reporting - Edit Template',
        displayedTitle: intl.formatMessage({
          id: 'reporting.reporting_edit_template',
          defaultMessage: 'Reporting - Edit Template'
        })
      },
      // Forecasting
      {
        paths: [...BASE_PREDICT, ROUTES.FORECASTS],
        title: 'Forecasting - Home',
        displayedTitle: intl.formatMessage({ id: 'forecasting.home', defaultMessage: 'Forecasting - Home' })
      },
      {
        paths: [...BASE_PREDICT, ROUTES.FORECASTS, ROUTES.FORECAST_BY_ID, ROUTES.PREDICTIONS, ROUTES.INVOICES],
        title: 'Forecasting - Invoices',
        displayedTitle: intl.formatMessage({ id: 'forecasting.invoices', defaultMessage: 'Forecasting - Invoices' })
      },
      {
        paths: [...BASE_PREDICT, ROUTES.FORECASTS, ROUTES.FORECAST_BY_ID, ROUTES.PREDICTIONS, ROUTES.SALES],
        title: 'Forecasting - Sales',
        displayedTitle: intl.formatMessage({ id: 'forecasting.sales', defaultMessage: 'Forecasting - Sales' })
      },
      {
        paths: [...BASE_PREDICT, ROUTES.FORECASTS, ROUTES.FORECAST_BY_ID, ROUTES.PREDICTIONS, ROUTES.COSTS],
        title: 'Forecasting - Costs',
        displayedTitle: intl.formatMessage({ id: 'forecasting.costs', defaultMessage: 'Forecasting - Costs' })
      },
      {
        paths: [...BASE_PREDICT, ROUTES.FORECASTS, ROUTES.FORECAST_BY_ID, ROUTES.PREDICTIONS, ROUTES.EXPENSES],
        title: 'Forecasting - Expenses',
        displayedTitle: intl.formatMessage({ id: 'forecasting.expenses', defaultMessage: 'Forecasting - Expenses' })
      },
      {
        paths: [...BASE_PREDICT, ROUTES.FORECASTS, ROUTES.FORECAST_BY_ID, ROUTES.PREDICTIONS, ROUTES.OTHER_P_AND_L],
        title: 'Forecasting - Other profit & loss',
        displayedTitle: intl.formatMessage({
          id: 'forecasting.other_profit_and_loss',
          defaultMessage: 'Forecasting - Other profit & loss'
        })
      },
      {
        paths: [...BASE_PREDICT, ROUTES.FORECASTS, ROUTES.FORECAST_BY_ID, ROUTES.PREDICTIONS, ROUTES.ASSETS],
        title: 'Forecasting - Assets',
        displayedTitle: intl.formatMessage({ id: 'forecasting.assets', defaultMessage: 'Forecasting - Assets' })
      },
      {
        paths: [...BASE_PREDICT, ROUTES.FORECASTS, ROUTES.FORECAST_BY_ID, ROUTES.PREDICTIONS, ROUTES.LIABILITIES],
        title: 'Forecasting - Liabilities',
        displayedTitle: intl.formatMessage({
          id: 'forecasting.liabilities',
          defaultMessage: 'Forecasting - Liabilities'
        })
      },
      {
        paths: [...BASE_PREDICT, ROUTES.FORECASTS, ROUTES.FORECAST_BY_ID, ROUTES.PREDICTIONS, ROUTES.PROFIT_AND_LOSS],
        title: 'Forecasting - Profit & Loss',
        displayedTitle: intl.formatMessage({
          id: 'forecasting.profit_and_loss',
          defaultMessage: 'Forecasting - Profit & Loss'
        })
      },
      {
        paths: [...BASE_PREDICT, ROUTES.FORECASTS, ROUTES.FORECAST_BY_ID, ROUTES.PREDICTIONS, ROUTES.BALANCE_SHEET],
        title: 'Forecasting - Balance Sheet',
        displayedTitle: intl.formatMessage({
          id: 'forecasting.balance_sheet',
          defaultMessage: 'Forecasting - Balance Sheet'
        })
      },
      {
        paths: [
          ...BASE_PREDICT,
          ROUTES.FORECASTS,
          ROUTES.FORECAST_BY_ID,
          ROUTES.PREDICTIONS,
          ROUTES.CASH_FLOW_STATEMENT
        ],
        title: 'Forecasting - Cash Flow Statement',
        displayedTitle: intl.formatMessage({
          id: 'forecasting.cash_flow_statement',
          defaultMessage: 'Forecasting - Cash Flow Statement'
        })
      },
      {
        paths: [...BASE_PREDICT, ROUTES.CHART_OF_ACCOUNTS, ROUTES.PROFIT_AND_LOSS],
        title: 'Chart of Accounts P&L',
        displayedTitle: intl.formatMessage({ id: 'chart_of_accounts.p_and_l', defaultMessage: 'Chart of Accounts P&L' })
      },
      {
        paths: [...BASE_PREDICT, ROUTES.CHART_OF_ACCOUNTS, ROUTES.BALANCE_SHEET],
        title: 'Chart of Accounts BS',
        displayedTitle: intl.formatMessage({ id: 'chart_of_accounts.bs', defaultMessage: 'Chart of Accounts BS' })
      },
      {
        paths: [...BASE_PREDICT_FORECAST, ROUTES.IMPORT],
        title: 'Futrli - Import',
        displayedTitle: intl.formatMessage({ id: 'import.import', defaultMessage: 'Futrli - Import' })
      }
    ],
    [BASE_PREDICT, BASE_PREDICT_FORECAST, intl]
  );

  const urls: TrackedUrl[] = useMemo(
    () => [
      ...UNAUTHENTICATED_PAGE_TRACKER_URLS,
      ...PREDICT_PAGE_TRACKER_URLS,
      {
        paths: [ROUTES.WORKSPACE_BY_ID, ROUTES.PAYMENT_DETAILS],
        title: 'Payment details - Invite',
        displayedTitle: intl.formatMessage({
          id: 'payment_details.invite',
          defaultMessage: 'Payment details - Invite'
        })
      },
      {
        paths: [ROUTES.INTEGRATIONS_ONBOARDING_ROUTE],
        title: 'Onboarding - Select Initial Data Source',
        displayedTitle: intl.formatMessage({
          id: 'onboarding.select_initial_data_source',
          defaultMessage: 'Onboarding - Select Initial Data Source'
        }),
        params: {
          blacklist: ['oauth_token']
        }
      }
    ],
    [PREDICT_PAGE_TRACKER_URLS, UNAUTHENTICATED_PAGE_TRACKER_URLS, intl]
  );

  /**
   * Handles the page tracking for the current location passed as param.
   * @param {Location} location
   */
  const handlePageChange = useCallback(
    (location: Location) => {
      const matchingItem = urls.find(url => findPathMatch(url, location));

      if (!matchingItem) {
        // Adds the default page title if the page has not got aliased title
        document.title = DEFAULT_PAGE_TITLE;
        return;
      }

      if (hasBlacklistedParam(matchingItem, location.search)) {
        return;
      }

      const title = matchingItem.interpolateTitleParams
        ? getInterpolatedTitle(matchingItem, location)
        : matchingItem.title;

      // We only track this page if it's not the same as the last one we
      // tracked. This helps avoid unwanted duplicate events when the app uses
      // query params.
      if (sanitazeStr(title) !== sanitazeStr(lastPageEventTitle)) {
        // Needed for HubSpot, @link https://community.hubspot.com/t5/APIs-Integrations/Track-Page-View-title-issue/m-p/323711#M31245
        document.title = matchingItem.displayedTitle;
        tracker.page(undefined, title, { title });

        lastPageEventTitle = title;
      }
    },
    [urls]
  );

  useEffect(() => {
    const isUnathenticatedPath = Boolean(UNAUTHENTICATED_PAGE_TRACKER_URLS.find(url => findPathMatch(url, location)));

    // Unauthenticated paths should not be tracked if user is authenticated, and vice versa.
    if (isUnathenticatedPath === !isAutheticated) {
      handlePageChange(location);
    }
  }, [location, isAutheticated, handlePageChange, UNAUTHENTICATED_PAGE_TRACKER_URLS]);

  return null;
};
export const GlobalPageTracker = React.memo(GlobalPageTrackerFC);
